import { Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { AdjPriorYearDataService } from './adj-prior-year-data.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-adj-prior-year-data',
    templateUrl: './adj-prior-year-data.component.html',
    styleUrls: ['./adj-prior-year-data.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AdjPriorYearDataComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();
  private isInitialized = false;

  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  modalTitle = 'Adjust Beginning Balances(Local)';

  rowData: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};

  scenarios: any[] = [];
  currencies: any[] = [];
  selected_curr_exg_rate: number | null = null;
  selected_scenario: any = null;
  selected_currency: any = null;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private adjPriorYearService: AdjPriorYearDataService,
    private gtwSnackbar: GtwSnackbarService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();

    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));

    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));

    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);

    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);

    this.cdr.detectChanges();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG?.base_url || !this.rowData || Object.keys(this.rowData).length === 0) return;
    this.isInitialized = true;
    this.loadScenarios();
    this.loadCurrencies();
    this.cdr.detectChanges();
  }

  private loadScenarios(): void {
    this.adjPriorYearService.getScenarios(
      this.GENERAL_CONFIG.base_url,
      this.rowData.PY_GROUP_OBJ_KEY ?? '',
      { globalParams: this.globalService },
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (list) => {
        this.scenarios = list || [];
        this.selected_scenario = this.scenarios.find((s: any) => s.GROUP_OBJ_KEY === this.rowData.PY_GROUP_OBJ_KEY) ?? this.scenarios[0] ?? null;
        this.cdr.detectChanges();
      },
      error: () => this.cdr.detectChanges()
    });
  }

  private loadCurrencies(): void {
    this.adjPriorYearService.getCurrencies(
      this.GENERAL_CONFIG.base_url,
      this.rowData.CURR ?? '',
      { globalParams: this.globalService },
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (list) => {
        this.currencies = list || [];
        this.selected_currency = this.currencies.find((c: any) => c.CURR === this.rowData.PY_CURR) ?? this.currencies[0] ?? null;
        this.selected_curr_exg_rate = this.selected_currency?.WTD_AVG_RATE ?? null;
        this.cdr.detectChanges();
      },
      error: () => this.cdr.detectChanges()
    });
  }

  changeCurrency(): void {
    this.selected_curr_exg_rate = this.selected_currency?.WTD_AVG_RATE ?? null;
    this.cdr.detectChanges();
  }

  save(): void {
    if (this.crudLoading) {
      this.gtwSnackbar.openSnackBar('Please wait while we save this request', 'gtw-snackbar--orange', true, 4);
      return;
    }
    if (!this.selected_scenario?.GROUP_OBJ_KEY) {
      this.gtwSnackbar.openSnackBar('Please select a scenario', 'gtw-snackbar--yellow', true, 4);
      return;
    }
    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const jsonObj = [{
      CY_COMBINATION_KEY: this.rowData.COMBINATION_KEY,
      PY_COMBINATION_KEY: this.rowData.PY_COMBINATION_KEY,
      OLD_PY_GROUP_OBJ_KEY: this.rowData.PY_GROUP_OBJ_KEY,
      NEW_PY_GROUP_OBJ_KEY: this.selected_scenario.GROUP_OBJ_KEY
    }];

    this.adjPriorYearService.updatePriorYearScenario(
      this.GENERAL_CONFIG.base_url,
      {},
      jsonObj,
      { globalParams: this.globalService },
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        if (result?.errorMessage && result.errorMessage !== 'null') {
          this.gtwSnackbar.openSnackBar(result.errorMessage, 'gtw-snackbar--red', true, 4);
        } else {
          this.gtwSnackbar.openSnackBar('Scenario has been updated successfully', 'gtw-snackbar--green', true, 4);
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.gtwSnackbar.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red', true, 4);
        this.cdr.detectChanges();
      }
    });
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
